import { createContext, useContext, useState } from 'react';
import { css, Global } from '@emotion/react';

const STORAGE_KEY = 'textSize';
const BASE_FONT_SIZE_PX = 16;

export const textSizeOptions = [
  { label: 'A', scale: 1, description: 'Normal text size' },
  { label: 'A+', scale: 1.25, description: 'Larger text size' },
  { label: 'A++', scale: 1.5, description: 'Largest text size' },
];

const DEFAULT_SCALE = textSizeOptions[0].scale;

const isKnownScale = (scale) =>
  textSizeOptions.some((option) => option.scale === scale);

const readScaleFromStorage = () => {
  const stored = Number(localStorage.getItem(STORAGE_KEY));
  return isKnownScale(stored) ? stored : DEFAULT_SCALE;
};

const TextSizeContext = createContext(null);

/**
 * Scales all `rem`-based typography for the routes it wraps by overriding the root
 * font size. `!important` is required because the app-wide "Increase text size"
 * setting writes an inline font-size onto <html> (App.js), which would otherwise win.
 * At the default scale we emit nothing, so that setting keeps its effect.
 */
const TextSizeProvider = ({ children }) => {
  const [scale, setScale] = useState(readScaleFromStorage);

  const saveScale = (nextScale) => {
    if (!isKnownScale(nextScale)) return;
    localStorage.setItem(STORAGE_KEY, String(nextScale));
    setScale(nextScale);
  };

  const isScaled = scale !== DEFAULT_SCALE;

  return (
    <TextSizeContext.Provider value={{ scale, saveScale }}>
      {isScaled && (
        <Global
          styles={css`
            html {
              font-size: ${scale * BASE_FONT_SIZE_PX}px !important;
            }
          `}
        />
      )}
      {children}
    </TextSizeContext.Provider>
  );
};

const useTextSize = () => useContext(TextSizeContext);

export { TextSizeProvider, useTextSize };
